/**
 * File: VeritectAIVisualization.jsx
 * Path: /src/components/home/VeritectAIVisualization.jsx
 * Description: Interactive AI visualization component for showcasing Veritect's legal AI capabilities.
 * Key Features: Neural network visualization, animated data flows, and real-time metrics display.
 * Maintenance Notes: Update metrics or visual elements when products or features change.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const VeritectAIVisualization = ({ activeTab = 'research' }) => {
  // State for animation triggers
  const [analyzeActive, setAnalyzeActive] = useState(false);
  const [pulseNodes, setPulseNodes] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  // Metrics based on active tab
  const metrics = {
    research: {
      accuracy: 88,
      speed: 92,
      relevance: 85,
      title: "Legal Research AI",
      color: "from-blue-500 to-indigo-600"
    },
    contract: {
      accuracy: 92,
      speed: 87,
      relevance: 90,
      title: "Contract Analysis",
      color: "from-blue-600 to-purple-600"
    },
    predict: {
      accuracy: 78,
      speed: 95,
      relevance: 82,
      title: "Judgment Prediction",
      color: "from-indigo-600 to-purple-600"
    }
  };

  const currentMetrics = metrics[activeTab];
  
  // Reset and trigger animations when tab changes
  useEffect(() => {
    // Reset
    setAnalyzeActive(false);
    setPulseNodes(false);
    setShowResults(false);
    
    // Sequence animations
    const sequence = async () => {
      await new Promise(r => setTimeout(r, 300));
      setAnalyzeActive(true);
      await new Promise(r => setTimeout(r, 1200));
      setPulseNodes(true);
      await new Promise(r => setTimeout(r, 1500));
      setShowResults(true);
    };
    
    sequence();
  }, [activeTab]);

  return (
    <div className="w-full h-64 md:h-80 flex items-center justify-center rounded-lg bg-white shadow-lg p-6 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-900/80 to-secondary-900/80 z-0"></div>
      
      {/* Circuit pattern overlay */}
      <div className="absolute inset-0 opacity-10 z-0">
        <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
          <g fill="none" stroke="white" strokeWidth="1.5">
            <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" />
            <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" />
            <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" />
            <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" />
          </g>
        </svg>
      </div>
      
      {/* Floating decorative elements */}
      <motion.div 
        className="absolute top-4 right-8 w-16 h-16 bg-primary-400/20 rounded-full blur-xl"
        animate={{ 
          y: [0, -15, 0], 
          opacity: [0.4, 0.7, 0.4] 
        }}
        transition={{ 
          duration: 5, 
          repeat: Infinity, 
          repeatType: "reverse" 
        }}
      />
      
      <motion.div 
        className="absolute bottom-8 left-8 w-24 h-24 bg-accent-400/20 rounded-full blur-xl"
        animate={{ 
          y: [0, 15, 0], 
          opacity: [0.3, 0.6, 0.3] 
        }}
        transition={{ 
          duration: 7, 
          repeat: Infinity, 
          repeatType: "reverse", 
          delay: 1 
        }}
      />
      
      <div className="h-full w-full flex items-center justify-center relative z-10">
        <div className="relative w-full max-w-xl">
          {/* Header with glowing effect */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <motion.div 
                className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center mr-4"
                animate={{ 
                  boxShadow: analyzeActive ? ['0 0 0px rgba(59, 130, 246, 0)', '0 0 20px rgba(59, 130, 246, 0.7)', '0 0 5px rgba(59, 130, 246, 0.5)'] : '0 0 0px rgba(59, 130, 246, 0)' 
                }}
                transition={{ duration: 2, repeat: analyzeActive ? Infinity : 0, repeatType: "reverse" }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </motion.div>
              <h3 className="font-bold text-xl text-white">Veritect AI {currentMetrics.title}</h3>
            </div>
            <div className="flex space-x-2">
              <motion.div 
                className="w-3 h-3 bg-primary-400 rounded-full"
                animate={{ opacity: analyzeActive ? [0.6, 1, 0.6] : 0.4 }}
                transition={{ duration: 1.5, repeat: analyzeActive ? Infinity : 0 }}
              />
              <motion.div 
                className="w-3 h-3 bg-accent-400 rounded-full"
                animate={{ opacity: analyzeActive ? [0.6, 1, 0.6] : 0.4 }}
                transition={{ duration: 1.5, delay: 0.5, repeat: analyzeActive ? Infinity : 0 }}
              />
              <motion.div 
                className="w-3 h-3 bg-secondary-400 rounded-full"
                animate={{ opacity: analyzeActive ? [0.6, 1, 0.6] : 0.4 }}
                transition={{ duration: 1.5, delay: 1, repeat: analyzeActive ? Infinity : 0 }}
              />
            </div>
          </div>
          
          {/* Neural network visualization */}
          <div className="mb-6 relative h-24 flex items-center justify-center">
            <svg width="360" height="90" viewBox="0 0 360 90" xmlns="http://www.w3.org/2000/svg">
              <g>
                {/* Input layer */}
                <motion.circle 
                  cx="30" cy="20" r="6" 
                  fill="rgba(255,255,255,0.8)"
                  animate={{ 
                    r: pulseNodes ? [6, 8, 6] : 6,
                    opacity: pulseNodes ? [0.8, 1, 0.8] : 0.8,
                    fill: pulseNodes ? ['rgba(255,255,255,0.8)', 'rgba(147,197,253,0.9)', 'rgba(255,255,255,0.8)'] : 'rgba(255,255,255,0.8)'
                  }}
                  transition={{ duration: 2, repeat: pulseNodes ? Infinity : 0, repeatType: "reverse", delay: 0.1 }}
                />
                <motion.circle 
                  cx="30" cy="45" r="6" 
                  fill="rgba(255,255,255,0.8)"
                  animate={{ 
                    r: pulseNodes ? [6, 8, 6] : 6,
                    opacity: pulseNodes ? [0.8, 1, 0.8] : 0.8,
                    fill: pulseNodes ? ['rgba(255,255,255,0.8)', 'rgba(147,197,253,0.9)', 'rgba(255,255,255,0.8)'] : 'rgba(255,255,255,0.8)'
                  }}
                  transition={{ duration: 2, repeat: pulseNodes ? Infinity : 0, repeatType: "reverse", delay: 0.3 }}
                />
                <motion.circle 
                  cx="30" cy="70" r="6" 
                  fill="rgba(255,255,255,0.8)"
                  animate={{ 
                    r: pulseNodes ? [6, 8, 6] : 6,
                    opacity: pulseNodes ? [0.8, 1, 0.8] : 0.8,
                    fill: pulseNodes ? ['rgba(255,255,255,0.8)', 'rgba(147,197,253,0.9)', 'rgba(255,255,255,0.8)'] : 'rgba(255,255,255,0.8)'
                  }}
                  transition={{ duration: 2, repeat: pulseNodes ? Infinity : 0, repeatType: "reverse", delay: 0.5 }}
                />
                
                {/* Hidden layer 1 */}
                <motion.circle 
                  cx="120" cy="15" r="6" 
                  fill="rgba(59,130,246,0.8)"
                  animate={{ 
                    r: pulseNodes ? [6, 8, 6] : 6,
                    opacity: pulseNodes ? [0.8, 1, 0.8] : 0.8,
                    fill: pulseNodes ? ['rgba(59,130,246,0.8)', 'rgba(96,165,250,0.9)', 'rgba(59,130,246,0.8)'] : 'rgba(59,130,246,0.8)'
                  }}
                  transition={{ duration: 2, repeat: pulseNodes ? Infinity : 0, repeatType: "reverse", delay: 0.6 }}
                />
                <motion.circle 
                  cx="120" cy="40" r="6" 
                  fill="rgba(59,130,246,0.8)"
                  animate={{ 
                    r: pulseNodes ? [6, 8, 6] : 6,
                    opacity: pulseNodes ? [0.8, 1, 0.8] : 0.8,
                    fill: pulseNodes ? ['rgba(59,130,246,0.8)', 'rgba(96,165,250,0.9)', 'rgba(59,130,246,0.8)'] : 'rgba(59,130,246,0.8)'
                  }}
                  transition={{ duration: 2, repeat: pulseNodes ? Infinity : 0, repeatType: "reverse", delay: 0.8 }}
                />
                <motion.circle 
                  cx="120" cy="65" r="6" 
                  fill="rgba(59,130,246,0.8)"
                  animate={{ 
                    r: pulseNodes ? [6, 8, 6] : 6,
                    opacity: pulseNodes ? [0.8, 1, 0.8] : 0.8,
                    fill: pulseNodes ? ['rgba(59,130,246,0.8)', 'rgba(96,165,250,0.9)', 'rgba(59,130,246,0.8)'] : 'rgba(59,130,246,0.8)'
                  }}
                  transition={{ duration: 2, repeat: pulseNodes ? Infinity : 0, repeatType: "reverse", delay: 1 }}
                />
                
                {/* Hidden layer 2 */}
                <motion.circle 
                  cx="210" cy="25" r="6" 
                  fill="rgba(139,92,246,0.8)"
                  animate={{ 
                    r: pulseNodes ? [6, 8, 6] : 6,
                    opacity: pulseNodes ? [0.8, 1, 0.8] : 0.8,
                    fill: pulseNodes ? ['rgba(139,92,246,0.8)', 'rgba(167,139,250,0.9)', 'rgba(139,92,246,0.8)'] : 'rgba(139,92,246,0.8)'
                  }}
                  transition={{ duration: 2, repeat: pulseNodes ? Infinity : 0, repeatType: "reverse", delay: 1.1 }}
                />
                <motion.circle 
                  cx="210" cy="50" r="6" 
                  fill="rgba(139,92,246,0.8)"
                  animate={{ 
                    r: pulseNodes ? [6, 8, 6] : 6,
                    opacity: pulseNodes ? [0.8, 1, 0.8] : 0.8,
                    fill: pulseNodes ? ['rgba(139,92,246,0.8)', 'rgba(167,139,250,0.9)', 'rgba(139,92,246,0.8)'] : 'rgba(139,92,246,0.8)'
                  }}
                  transition={{ duration: 2, repeat: pulseNodes ? Infinity : 0, repeatType: "reverse", delay: 1.3 }}
                />
                <motion.circle 
                  cx="210" cy="75" r="6" 
                  fill="rgba(139,92,246,0.8)"
                  animate={{ 
                    r: pulseNodes ? [6, 8, 6] : 6,
                    opacity: pulseNodes ? [0.8, 1, 0.8] : 0.8,
                    fill: pulseNodes ? ['rgba(139,92,246,0.8)', 'rgba(167,139,250,0.9)', 'rgba(139,92,246,0.8)'] : 'rgba(139,92,246,0.8)'
                  }}
                  transition={{ duration: 2, repeat: pulseNodes ? Infinity : 0, repeatType: "reverse", delay: 1.5 }}
                />
                
                {/* Output */}
                <motion.circle 
                  cx="300" cy="30" r="6" 
                  fill="rgba(234,179,8,0.8)"
                  animate={{ 
                    r: pulseNodes ? [6, 8, 6] : 6,
                    opacity: pulseNodes ? [0.8, 1, 0.8] : 0.8,
                    fill: pulseNodes ? ['rgba(234,179,8,0.8)', 'rgba(250,204,21,0.9)', 'rgba(234,179,8,0.8)'] : 'rgba(234,179,8,0.8)'
                  }}
                  transition={{ duration: 2, repeat: pulseNodes ? Infinity : 0, repeatType: "reverse", delay: 1.6 }}
                />
                <motion.circle 
                  cx="300" cy="60" r="6" 
                  fill="rgba(234,179,8,0.8)"
                  animate={{ 
                    r: pulseNodes ? [6, 8, 6] : 6,
                    opacity: pulseNodes ? [0.8, 1, 0.8] : 0.8,
                    fill: pulseNodes ? ['rgba(234,179,8,0.8)', 'rgba(250,204,21,0.9)', 'rgba(234,179,8,0.8)'] : 'rgba(234,179,8,0.8)'
                  }}
                  transition={{ duration: 2, repeat: pulseNodes ? Infinity : 0, repeatType: "reverse", delay: 1.8 }}
                />
                
                {/* Connections */}
                <g>
                  {/* Input to hidden 1 connections */}
                  {[
                    { x1: 36, y1: 20, x2: 114, y2: 15, delay: 0.1 },
                    { x1: 36, y1: 20, x2: 114, y2: 40, delay: 0.2 },
                    { x1: 36, y1: 20, x2: 114, y2: 65, delay: 0.3 },
                    { x1: 36, y1: 45, x2: 114, y2: 15, delay: 0.25 },
                    { x1: 36, y1: 45, x2: 114, y2: 40, delay: 0.35 },
                    { x1: 36, y1: 45, x2: 114, y2: 65, delay: 0.45 },
                    { x1: 36, y1: 70, x2: 114, y2: 15, delay: 0.4 },
                    { x1: 36, y1: 70, x2: 114, y2: 40, delay: 0.5 },
                    { x1: 36, y1: 70, x2: 114, y2: 65, delay: 0.6 }
                  ].map((line, index) => (
                    <motion.line 
                      key={`line1-${index}`}
                      x1={line.x1} 
                      y1={line.y1} 
                      x2={line.x2} 
                      y2={line.y2} 
                      stroke="rgba(255,255,255,0.3)"
                      strokeWidth="1"
                      animate={analyzeActive ? {
                        stroke: ['rgba(255,255,255,0.3)', 'rgba(59,130,246,0.9)', 'rgba(255,255,255,0.3)'],
                        strokeWidth: [1, 2, 1]
                      } : {}}
                      transition={{ 
                        duration: 0.8, 
                        delay: line.delay,
                        repeat: analyzeActive ? Infinity : 0,
                        repeatDelay: 3
                      }}
                    />
                  ))}
                  
                  {/* Hidden 1 to hidden 2 connections */}
                  {[
                    { x1: 126, y1: 15, x2: 204, y2: 25, delay: 0.7 },
                    { x1: 126, y1: 15, x2: 204, y2: 50, delay: 0.8 },
                    { x1: 126, y1: 15, x2: 204, y2: 75, delay: 0.9 },
                    { x1: 126, y1: 40, x2: 204, y2: 25, delay: 0.85 },
                    { x1: 126, y1: 40, x2: 204, y2: 50, delay: 0.95 },
                    { x1: 126, y1: 40, x2: 204, y2: 75, delay: 1.05 },
                    { x1: 126, y1: 65, x2: 204, y2: 25, delay: 1 },
                    { x1: 126, y1: 65, x2: 204, y2: 50, delay: 1.1 },
                    { x1: 126, y1: 65, x2: 204, y2: 75, delay: 1.2 }
                  ].map((line, index) => (
                    <motion.line 
                      key={`line2-${index}`}
                      x1={line.x1} 
                      y1={line.y1} 
                      x2={line.x2} 
                      y2={line.y2} 
                      stroke="rgba(255,255,255,0.3)"
                      strokeWidth="1"
                      animate={analyzeActive ? {
                        stroke: ['rgba(255,255,255,0.3)', 'rgba(139,92,246,0.9)', 'rgba(255,255,255,0.3)'],
                        strokeWidth: [1, 2, 1]
                      } : {}}
                      transition={{ 
                        duration: 0.8, 
                        delay: line.delay,
                        repeat: analyzeActive ? Infinity : 0,
                        repeatDelay: 3
                      }}
                    />
                  ))}
                  
                  {/* Hidden 2 to output connections */}
                  {[
                    { x1: 216, y1: 25, x2: 294, y2: 30, delay: 1.3 },
                    { x1: 216, y1: 25, x2: 294, y2: 60, delay: 1.4 },
                    { x1: 216, y1: 50, x2: 294, y2: 30, delay: 1.5 },
                    { x1: 216, y1: 50, x2: 294, y2: 60, delay: 1.6 },
                    { x1: 216, y1: 75, x2: 294, y2: 30, delay: 1.7 },
                    { x1: 216, y1: 75, x2: 294, y2: 60, delay: 1.8 }
                  ].map((line, index) => (
                    <motion.line 
                      key={`line3-${index}`}
                      x1={line.x1} 
                      y1={line.y1} 
                      x2={line.x2} 
                      y2={line.y2} 
                      stroke="rgba(255,255,255,0.3)"
                      strokeWidth="1"
                      animate={analyzeActive ? {
                        stroke: ['rgba(255,255,255,0.3)', 'rgba(234,179,8,0.9)', 'rgba(255,255,255,0.3)'],
                        strokeWidth: [1, 2, 1]
                      } : {}}
                      transition={{ 
                        duration: 0.8, 
                        delay: line.delay,
                        repeat: analyzeActive ? Infinity : 0,
                        repeatDelay: 3
                      }}
                    />
                  ))}
                </g>
              </g>
            </svg>
          </div>
          
          {/* Results/output section */}
          <motion.div 
            className="flex space-x-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: showResults ? 1 : 0, y: showResults ? 0 : 20 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex-1 bg-white/5 backdrop-blur-sm rounded-lg p-3">
              <div className="text-xs text-white/60 mb-2">Accuracy</div>
              <div className="w-full h-2 bg-white/20 rounded-full mb-2 overflow-hidden">
                <motion.div 
                  className={`h-2 bg-gradient-to-r ${currentMetrics.color} rounded-full`}
                  initial={{ width: 0 }}
                  animate={{ width: showResults ? `${currentMetrics.accuracy}%` : 0 }}
                  transition={{ duration: 1, delay: 0.1 }}
                ></motion.div>
              </div>
              <div className="text-right text-xs font-medium text-white">{currentMetrics.accuracy}%</div>
            </div>
            <div className="flex-1 bg-white/5 backdrop-blur-sm rounded-lg p-3">
              <div className="text-xs text-white/60 mb-2">Speed</div>
              <div className="w-full h-2 bg-white/20 rounded-full mb-2 overflow-hidden">
                <motion.div 
                  className={`h-2 bg-gradient-to-r ${currentMetrics.color} rounded-full`}
                  initial={{ width: 0 }}
                  animate={{ width: showResults ? `${currentMetrics.speed}%` : 0 }}
                  transition={{ duration: 1, delay: 0.2 }}
                ></motion.div>
              </div>
              <div className="text-right text-xs font-medium text-white">{currentMetrics.speed}%</div>
            </div>
            <div className="flex-1 bg-white/5 backdrop-blur-sm rounded-lg p-3">
              <div className="text-xs text-white/60 mb-2">Relevance</div>
              <div className="w-full h-2 bg-white/20 rounded-full mb-2 overflow-hidden">
                <motion.div 
                  className={`h-2 bg-gradient-to-r ${currentMetrics.color} rounded-full`}
                  initial={{ width: 0 }}
                  animate={{ width: showResults ? `${currentMetrics.relevance}%` : 0 }}
                  transition={{ duration: 1, delay: 0.3 }}
                ></motion.div>
              </div>
              <div className="text-right text-xs font-medium text-white">{currentMetrics.relevance}%</div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default VeritectAIVisualization;